import { createSignal, createEffect, Show } from 'solid-js';
import { useNavigate } from '@solidjs/router';
import { useUserContext } from '../components/UserContextProvider';
import { useSFetch } from '../hooks/sfetch';
import Text from '../components/Text';
import TextWithCopyButton from '../components/TextWithCopyButton';
import { setFeatureFlags } from '../services/featureFlags';
import styles from './Onboarding.module.css';

const TRACK = [
    { labelKey: 'ONBOARDING_TRACK_VERIFY', icon: 'fa-solid fa-gamepad' },
    { labelKey: 'ONBOARDING_TRACK_ACCESS', icon: 'fa-solid fa-key' },
    { labelKey: 'ONBOARDING_TRACK_TEAM',   icon: 'fa-solid fa-building' },
] as const;

export default function Onboarding() {
    const navigate = useNavigate();
    const sfetch = useSFetch();
    const { user, setUser, userTenant, setUserTenant } = useUserContext();

    const [checking, setChecking] = createSignal(false);

    const completedSteps = () => {
        const perms = user()?.permissions ?? 0;
        return [
            true,         // Minecraft always verified before landing here
            perms > 0,    // Has at least Read permission
            userTenant() !== null,
        ];
    };

    const activeIndex = () => {
        const s = completedSteps();
        const idx = s.findIndex(done => !done);
        return idx === -1 ? s.length : idx;
    };

    createEffect(() => {
        if (activeIndex() >= TRACK.length) {
            navigate('/dashboard');
        }
    });

    async function checkAgain() {
        setChecking(true);
        try {
            const res = await sfetch('/api/User');
            if (res.ok) {
                const data = res.value;
                setUser({
                    ...user(),
                    username: data.username,
                    id: data.id,
                    uuid: data.minecraftUUID,
                    minecraftUsernameVerified: data.minecraftUsernameVerified,
                    permissions: data.permissions,
                    loggedin: true,
                });
                setFeatureFlags((res as any).featureFlags ?? null);

                const tenantRes = await sfetch('/api/User/tenant');
                if (tenantRes.ok) {
                    setUserTenant(tenantRes.value ?? null);
                }
            }
        } finally {
            setChecking(false);
        }
    }

    return (
        <div class={styles.page}>
            <div class={styles.container}>
                <div class={styles.header}>
                    <h1 class={styles.title}><Text val="ONBOARDING_TITLE" /></h1>
                    <p class={styles.subtitle}><Text val="ONBOARDING_SUBTITLE" /></p>
                </div>

                <div class={styles.layout}>
                    <div class={styles.stepTrack}>
                        {TRACK.map((step, i) => {
                            const done = () => completedSteps()[i];
                            const active = () => activeIndex() === i;
                            return (
                                <div
                                    class={styles.stepItem}
                                    classList={{ [styles.done]: done(), [styles.active]: active() }}
                                >
                                    <div class={styles.stepBubble}>
                                        <Show when={done()} fallback={<span>{i + 1}</span>}>
                                            <i class="fa-solid fa-check" />
                                        </Show>
                                    </div>
                                    <div class={styles.stepMeta}>
                                        <span class={styles.stepNumber}>
                                            <Text val="ONBOARDING_STEP" /> {i + 1}
                                        </span>
                                        <span class={styles.stepLabel}>
                                            <Text val={step.labelKey} />
                                        </span>
                                    </div>
                                    <Show when={i < TRACK.length - 1}>
                                        <div class={styles.stepConnector} />
                                    </Show>
                                </div>
                            );
                        })}
                    </div>

                    <div class={styles.content}>
                        {/* Step: waiting for admin to grant access */}
                        <Show when={activeIndex() === 1}>
                            <div class={styles.card}>
                                <div class={styles.cardIcon}>
                                    <i class="fa-solid fa-clock" />
                                </div>
                                <h2 class={styles.cardTitle}><Text val="ONBOARDING_ACCESS_TITLE" /></h2>
                                <p class={styles.cardDesc}><Text val="ONBOARDING_ACCESS_DESC" /></p>

                                <div class={styles.usernameBox}>
                                    <span class={styles.usernameLabel}><Text val="ONBOARDING_YOUR_USERNAME" /></span>
                                    <TextWithCopyButton text={user()?.username ?? ''} />
                                </div>

                                <div class={styles.cardActions}>
                                    <button class="btn btn-primary" onClick={checkAgain} disabled={checking()}>
                                        <Show when={checking()} fallback={<i class="fa-solid fa-arrows-rotate" />}>
                                            <i class="fa-solid fa-spinner fa-spin" />
                                        </Show>
                                        <Text val={checking() ? 'ONBOARDING_CHECKING' : 'ONBOARDING_CHECK_AGAIN'} />
                                    </button>
                                </div>

                                <div class={styles.hint}>
                                    <i class="fa-solid fa-circle-info" />
                                    <span><Text val="ONBOARDING_ACCESS_HINT" /></span>
                                </div>
                            </div>
                        </Show>

                        {/* Step: create or join a team */}
                        <Show when={activeIndex() === 2}>
                            <div class={styles.card}>
                                <div class={`${styles.cardIcon} ${styles.cardIconGreen}`}>
                                    <i class="fa-solid fa-building" />
                                </div>
                                <h2 class={styles.cardTitle}><Text val="ONBOARDING_TEAM_TITLE" /></h2>
                                <p class={styles.cardDesc}><Text val="ONBOARDING_TEAM_DESC" /></p>

                                <div class={styles.cardActions}>
                                    <a href="/tenant/new" class="btn btn-primary">
                                        <i class="fa-solid fa-plus" />
                                        <Text val="ONBOARDING_CREATE_TEAM" />
                                    </a>
                                    <button class="btn btn-secondary" onClick={checkAgain} disabled={checking()}>
                                        <Show when={checking()} fallback={<i class="fa-solid fa-arrows-rotate" />}>
                                            <i class="fa-solid fa-spinner fa-spin" />
                                        </Show>
                                        <Text val={checking() ? 'ONBOARDING_CHECKING' : 'ONBOARDING_CHECK_AGAIN'} />
                                    </button>
                                </div>

                                <div class={styles.hint}>
                                    <i class="fa-solid fa-circle-info" />
                                    <span><Text val="ONBOARDING_TEAM_HINT" /></span>
                                </div>
                            </div>
                        </Show>
                    </div>
                </div>
            </div>
        </div>
    );
}
